/**
 * Hook for child device session validation.
 * Validates on mount + on visibility change (tab focus).
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getChildSession, getDeviceIdHash, clearChildSession } from "@/lib/device";
import { sha256Hex } from "@/lib/crypto";

export function useChildDeviceSession() {
  const session = getChildSession();
  const queryClient = useQueryClient();
  const [invalidated, setInvalidated] = useState(false);

  const queryKey = ["child-device-session", session?.childId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!session) return null;
      const deviceIdHash = await getDeviceIdHash();
      const sessionTokenHash = await sha256Hex(session.sessionToken);

      const { data, error } = await supabase.rpc("validate_child_device", {
        _device_id_hash: deviceIdHash,
        _session_token_hash: sessionTokenHash,
      });

      if (error || !data || data.length === 0) {
        clearChildSession();
        setInvalidated(true);
        return null;
      }

      // Touch last_seen (best effort, no await)
      supabase.rpc("touch_child_device_session", {
        _device_id_hash: deviceIdHash,
        _session_token_hash: sessionTokenHash,
      });

      return { childId: data[0].child_id as string, familyId: data[0].family_id as string };
    },
    enabled: !!session,
    staleTime: 60_000,
  });

  // Revalidate on visibility change (tab focus / PWA resume)
  const revalidate = useCallback(() => {
    if (session) {
      queryClient.invalidateQueries({ queryKey });
    }
  }, [session, queryClient, queryKey]);

  useEffect(() => {
    function handleVisibility() {
      if (document.visibilityState === "visible") {
        revalidate();
      }
    }
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [revalidate]);

  return {
    isChildDevice: !!data,
    childId: data?.childId ?? session?.childId ?? null,
    familyId: data?.familyId ?? null,
    isLoading: !!session && isLoading,
    hasStoredSession: !!session,
    /** True when a stored session was validated and found invalid (revoked or expired) */
    invalidated,
  };
}
